import type { IntlShape } from 'react-intl';

import { ErrorKeySet, type ValidationError, type Validator } from '../../../types/field-validation';
import type { FormFieldValidation } from '../../../types/form/form-fields';
import type { DateTimeValue } from '../../../types/form/form-fields/field-values';
import { getDateValidationErrors } from '../date';
import { getTimeValidationErrors } from '../time';
import { getFailedRequiredValidationError } from '../utils';
import { messages } from './messages';

export const getDateTimeValidationErrors = (
	validation: FormFieldValidation,
	fieldLabel: string,
	intl?: IntlShape,
	dateTime?: DateTimeValue,
): ReturnType<Validator> => {
	if (Object.keys(validation).length === 0) {
		return [];
	}
	// we don't check for min/max date and time here, because in the frontend it is possible to have a Date field with Time validation and vice versa
	if (!dateTime) {
		if (validation.required) {
			return [getFailedRequiredValidationError(fieldLabel, intl)];
		}
		return [];
	}
	// DateTime values to be reused throughout the validation logic
	const minDateTime =
		validation.minimumDate && validation.minimumTime
			? new Date(`${validation.minimumDate}T${validation.minimumTime}`)
			: undefined;
	const maxDateTime =
		validation.maximumDate && validation.maximumTime
			? new Date(`${validation.maximumDate}T${validation.maximumTime}`)
			: undefined;
	const enteredDateTime =
		dateTime.date && dateTime.localTime
			? new Date(`${dateTime.date}T${dateTime.localTime}`)
			: dateTime.date
				? new Date(dateTime.date)
				: undefined;

	// Boolean values for validation logic
	const hasMinDateTimeRequirements = !!validation.minimumDate && !!validation.minimumTime;
	const hasMaxDateTimeRequirements = !!validation.maximumDate && !!validation.maximumTime;
	const hasExactDateTimeRequirements =
		hasMinDateTimeRequirements &&
		validation.minimumDate === validation.maximumDate &&
		validation.minimumTime === validation.maximumTime;
	const hasBetweenDateTimeRequirements = hasMinDateTimeRequirements && hasMaxDateTimeRequirements;

	// Date or time required
	if (validation.required && (!dateTime.date || !dateTime.localTime)) {
		return [
			{
				errorKey: ErrorKeySet.REQUIRED,
				errorMessage: intl?.formatMessage(messages.requiredDateTime, { fieldLabel }),
			},
		];
	}

	if (hasExactDateTimeRequirements && minDateTime && enteredDateTime) {
		if (enteredDateTime.valueOf() !== minDateTime.valueOf()) {
			return [
				{
					errorKey: ErrorKeySet.INVALID,
					errorMessage: intl?.formatMessage(messages.exactDateTime, {
						date: minDateTime,
						time: minDateTime,
					}),
				},
			];
		}
	} else if (hasBetweenDateTimeRequirements && minDateTime && maxDateTime && enteredDateTime) {
		if (enteredDateTime < minDateTime || enteredDateTime > maxDateTime) {
			return [
				{
					errorKey: ErrorKeySet.INVALID,
					errorMessage: intl?.formatMessage(messages.betweenDateTime, {
						minimumDate: minDateTime,
						minimumTime: minDateTime,
						maximumDate: maxDateTime,
						maximumTime: maxDateTime,
					}),
				},
			];
		}
	} else if (hasMinDateTimeRequirements || hasMaxDateTimeRequirements) {
		if (hasMinDateTimeRequirements && minDateTime && enteredDateTime) {
			if (enteredDateTime < minDateTime) {
				return [
					{
						errorKey: ErrorKeySet.INVALID,
						errorMessage: intl?.formatMessage(messages.minimumDateTime, {
							minimumDate: minDateTime,
							minimumTime: minDateTime,
						}),
					},
				];
			}
		}

		// Max Date and time
		if (hasMaxDateTimeRequirements && enteredDateTime && maxDateTime) {
			if (enteredDateTime > maxDateTime) {
				return [
					{
						errorKey: ErrorKeySet.INVALID,
						errorMessage: intl?.formatMessage(messages.maximumDateTime, {
							maximumDate: maxDateTime,
							maximumTime: maxDateTime,
						}),
					},
				];
			}
		}
	} else {
		// Individual date and time validations
		const validationErrors: ValidationError[] = [];

		// Validate Date
		if (dateTime.date) {
			const dateValidationErrors = getDateValidationErrors(
				validation,
				fieldLabel,
				intl,
				dateTime.date,
			);
			validationErrors.push(...dateValidationErrors);
		}

		if (dateTime.localTime) {
			// Validate Time
			const timeValidationErrors = getTimeValidationErrors(
				validation,
				fieldLabel,
				intl,
				dateTime.localTime,
			);
			validationErrors.push(...timeValidationErrors);
		}

		return validationErrors;
	}
	return [];
};
